import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, Edit, Plus, Upload, Image, FileText, FolderOpen, Download } from "lucide-react";

interface MediaFile {
  id: string;
  name: string;
  type: "image" | "document" | "other";
  size: number;
  url: string;
  category: string;
  description?: string;
  createdAt: string;
}

export function MediaManager() {
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [newMedia, setNewMedia] = useState({
    name: "",
    category: "general",
    description: "",
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [filter, setFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const categories = ["general", "speakers", "sponsors", "venue", "agenda", "about", "banners"];

  useEffect(() => {
    loadMediaFiles();
  }, []);

  const loadMediaFiles = async () => {
    try {
      // Mock data - replace with actual API call
      const mockFiles: MediaFile[] = [
        {
          id: "1",
          name: "speaker-1.jpg",
          type: "image",
          size: 245760,
          url: "/assets/images/speakers/speaker-1.jpg",
          category: "speakers",
          description: "Main conference speaker",
          createdAt: "2024-05-25T10:00:00Z",
        },
        {
          id: "2",
          name: "venue-map.pdf",
          type: "document",
          size: 1024000,
          url: "/assets/documents/venue-map.pdf",
          category: "venue",
          description: "Conference venue layout",
          createdAt: "2024-05-25T09:30:00Z",
        },
      ];
      setMediaFiles(mockFiles);
    } catch (error) {
      alert("Failed to load media files");
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setNewMedia({
        name: file.name,
        category: "general",
        description: "",
      });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert("Please select a file to upload");
      return;
    }

    setUploading(true);
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("category", newMedia.category);
      formData.append("description", newMedia.description);

      // Mock upload - replace with actual API call
      const newFile: MediaFile = {
        id: Date.now().toString(),
        name: selectedFile.name,
        type: selectedFile.type.startsWith("image/")
          ? "image"
          : selectedFile.type === "application/pdf"
            ? "document"
            : "other",
        size: selectedFile.size,
        url: `/assets/${newMedia.category}/${selectedFile.name}`,
        category: newMedia.category,
        description: newMedia.description,
        createdAt: new Date().toISOString(),
      };

      setMediaFiles((prev) => [newFile, ...prev]);
      setSelectedFile(null);
      setNewMedia({ name: "", category: "general", description: "" });
      setIsDialogOpen(false);
      alert("File uploaded successfully");
    } catch (error) {
      alert("Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this file?")) return;

    try {
      // Mock delete - replace with actual API call
      setMediaFiles((prev) => prev.filter((file) => file.id !== id));
      alert("File deleted successfully");
    } catch (error) {
      alert("Failed to delete file");
    }
  };

  const filteredFiles = mediaFiles.filter((file) => {
    const matchesFilter = filter === "all" || file.type === filter;
    const matchesSearch =
      file.name.toLowerCase().includes(search.toLowerCase()) ||
      file.description?.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case "image":
        return <Image className="w-4 h-4" />;
      case "document":
        return <FileText className="w-4 h-4" />;
      default:
        return <FolderOpen className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-serif text-3xl text-primary mb-2">Media Manager</h1>
          <p className="text-muted-foreground">
            Upload and manage images, documents, and other media files
          </p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Upload className="w-4 h-4 mr-2" />
              Upload File
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Upload Media File</DialogTitle>
              <DialogDescription>
                Upload and manage images, documents, and other media files for the event.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="file">Select File</Label>
                <Input
                  id="file"
                  type="file"
                  onChange={handleFileSelect}
                  accept="image/*,.pdf,.doc,.docx"
                />
              </div>

              {selectedFile && (
                <>
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={newMedia.category}
                      onValueChange={(value) =>
                        setNewMedia((prev) => ({ ...prev, category: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="description">Description (Optional)</Label>
                    <Textarea
                      id="description"
                      value={newMedia.description}
                      onChange={(e) =>
                        setNewMedia((prev) => ({ ...prev, description: e.target.value }))
                      }
                      placeholder="Enter file description"
                      rows={3}
                    />
                  </div>

                  <div className="text-sm text-muted-foreground">
                    <p>File: {selectedFile.name}</p>
                    <p>Size: {formatFileSize(selectedFile.size)}</p>
                    <p>Type: {selectedFile.type}</p>
                  </div>
                </>
              )}

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleUpload} disabled={!selectedFile || uploading}>
                  {uploading ? "Uploading..." : "Upload"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters and Search */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search media files..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Files</SelectItem>
              <SelectItem value="image">Images</SelectItem>
              <SelectItem value="document">Documents</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Media Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredFiles.map((file) => (
          <Card key={file.id} className="overflow-hidden">
            <div className="aspect-square bg-muted flex items-center justify-center">
              {file.type === "image" ? (
                <img
                  src={file.url}
                  alt={file.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = "/assets/placeholder-image.png";
                  }}
                />
              ) : (
                <div className="text-center p-4">
                  {getFileIcon(file.type)}
                  <p className="text-sm text-muted-foreground mt-2">{file.name}</p>
                </div>
              )}
            </div>
            <div className="p-4">
              <h3 className="font-medium text-sm truncate">{file.name}</h3>
              <p className="text-xs text-muted-foreground mb-2">
                {formatFileSize(file.size)} • {file.category}
              </p>
              {file.description && (
                <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                  {file.description}
                </p>
              )}
              <div className="flex justify-between items-center">
                <Button variant="outline" size="sm" asChild>
                  <a href={file.url} target="_blank" rel="noopener noreferrer">
                    <Download className="w-3 h-3" />
                  </a>
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(file.id)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {filteredFiles.length === 0 && (
        <Card className="p-8 text-center">
          <FolderOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No media files found</h3>
          <p className="text-muted-foreground mb-4">
            {search || filter !== "all"
              ? "Try adjusting your search or filters"
              : "Upload your first media file to get started"}
          </p>
          <Button onClick={() => setIsDialogOpen(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Upload First File
          </Button>
        </Card>
      )}
    </div>
  );
}
